import type { CalendarEventReminder, CalendarEventTemplate } from "@tellescope/types-models";
import type { EventFormInitialValues } from "../hooks/use-event-form-dialog-contents";

type TemplateFormUpdateValues = Pick<
  EventFormInitialValues,
  | "title"
  | "description"
  | "duration"
  | "startDate"
  | "enableVideoCall"
  | "generateZoomLinkOnCreate"
  | "dontSyncToCanvas"
  | "videoIntegration"
  | "zoomIntegration"
  | "enduserAttendeeLimit"
  | "displayTitle"
  | "displayDescription"
  | "instructions"
  | "allowGroupReschedule"
  | "requirePortalCancelReason"
  | "dontAutoSyncPatientToHealthie"
  | "healthieInsuranceBillingEnabled"
  | "bufferStartMinutes"
  | "bufferEndMinutes"
  | "useUserURL"
  | "preventCancelMinutesInAdvance"
  | "preventRescheduleMinutesInAdvance"
  | "preventCancelInPortal"
  | "preventRescheduleInPortal"
  | "athenaDepartmentId"
  | "athenaTypeId"
  | "athenaBookingTypeId"
  | "generateAthenaTelehealthLink"
  | "createAndBookAthenaSlot"
  | "dontSyncToElation"
  | "sendIcsEmail"
>;

export interface BuildTemplateFormUpdatesResult {
  updates: TemplateFormUpdateValues;
  reminders?: CalendarEventReminder[];
}

const ALL_DAY_DURATION_IN_MINUTES = 1440;

/**
 * Template fields that map 1:1 onto a form field of the same name and are applied only when the
 * template's value is truthy — so selecting a template never wipes an existing form entry.
 */
const TRUTHY_ONLY_TEMPLATE_KEYS = [
  "instructions",
  "preventCancelMinutesInAdvance",
  "preventRescheduleMinutesInAdvance",
  "preventCancelInPortal",
  "preventRescheduleInPortal",
  "athenaDepartmentId",
  "athenaTypeId",
  "athenaBookingTypeId",
  "generateAthenaTelehealthLink",
  "createAndBookAthenaSlot",
  "dontSyncToElation",
  "sendIcsEmail",
] as const satisfies readonly (keyof TemplateFormUpdateValues & keyof CalendarEventTemplate)[];

const startOfLocalDay = (date: Date): Date => {
  const midnight = new Date(date);
  midnight.setHours(0, 0, 0, 0);
  return midnight;
};

/**
 * Maps a selected calendar event template onto the fields an event form should update when that
 * template is chosen. Care-plan fields are intentionally ignored.
 *
 * Two rules govern how values are applied:
 * - Unconditional fields are always copied from the template, even when the template's value is falsy
 *   (so switching templates resets them).
 * - Truthy-only fields are copied only when the template has a truthy value; otherwise they are left
 *   out of `updates` so the caller keeps the existing form value.
 *
 * Reminders live outside the form, so they are returned separately and only when the template has them.
 */
export const buildTemplateFormUpdates = (
  template: CalendarEventTemplate,
  currentValues: Pick<EventFormInitialValues, "startDate">
): BuildTemplateFormUpdatesResult => {
  const updates = TRUTHY_ONLY_TEMPLATE_KEYS.reduce<TemplateFormUpdateValues>(
    (acc, key) => (template[key] ? { ...acc, [key]: template[key] } : acc),
    {
      title: template.displayTitle || template.title || "",
      description: template.displayDescription || template.description || "",
      duration: template.durationInMinutes,
      enableVideoCall: template.enableVideoCall || false,
      generateZoomLinkOnCreate: !!template.generateZoomLinkWhenBooked,
      dontSyncToCanvas: template.dontSyncToCanvas || false,
      videoIntegration: template.videoIntegration,
      zoomIntegration: template.videoIntegration === "Zoom",
      enduserAttendeeLimit: template.enduserAttendeeLimit,
      displayTitle: template.displayTitle || "",
      displayDescription: template.displayDescription || "",
      allowGroupReschedule: !!template.allowGroupReschedule,
      requirePortalCancelReason: !!template.requirePortalCancelReason,
      dontAutoSyncPatientToHealthie: template.dontAutoSyncPatientToHealthie || false,
      healthieInsuranceBillingEnabled: template.healthieInsuranceBillingEnabled || false,
      bufferStartMinutes: template.bufferStartMinutes,
      bufferEndMinutes: template.bufferEndMinutes,
      useUserURL: !!template.useUserURL,
      ...(template.durationInMinutes === ALL_DAY_DURATION_IN_MINUTES && currentValues.startDate
        ? { startDate: startOfLocalDay(currentValues.startDate) }
        : {}),
    }
  );

  return {
    updates,
    ...(template.reminders ? { reminders: template.reminders } : {}),
  };
};
